/**
 * HelpPanel Component
 * Displays contextual help content in a panel format
 * Pattern: Atomic Design - Organisms
 */

import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionItem,
  ArrowForwardIcon,
  ArticleIcon,
  Chip,
  Divider,
  Link,
  Text,
} from '@billda/design-system';
import type { HelpContent } from '../../types/helpContent.types';
import { Routes } from '../../../routes';

export interface HelpPanelProps {
  helpContent: HelpContent;
  'data-testid'?: string;
}

export function HelpPanel({
  helpContent,
  'data-testid': dataTestId = 'help-panel',
}: HelpPanelProps) {
  const navigate = useNavigate();

  // Map page titles to routes
  const getRouteForPage = (title: string): string | null => {
    const routeMap: Record<string, string> = {
      Gangs: Routes.gangs.manage,
      Users: Routes.users.manage,
      'Labour Groups': Routes.labour.groupsManage,
      'Labour Item Groups': Routes.labour.groupsManage,
      'Labour Items': Routes.labour.itemsManage,
      'Labour Item Definitions': Routes.labour.itemsManage,
      Sites: Routes.sites.manage,
      Finance: Routes.finance.pots,
      'Pot Approvals': Routes.finance.pots, // Backwards compatibility
    };
    return routeMap[title] || null;
  };

  const handlePageClick = (title: string) => {
    const route = getRouteForPage(title);
    if (route) {
      navigate(route);
    }
  };

  const sectionHeading = 'mb-3 font-semibold';

  return (
    <div className="px-3 pb-3 pt-1" data-testid={dataTestId}>
      {/* Main Description */}
      <Text variant="body1" color="secondary" className="mb-3 leading-relaxed">
        {helpContent.description}
      </Text>

      <Divider className="mb-3" />

      {/* What is it? Section */}
      {helpContent.sections.whatIsIt && (
        <>
          <Text as="h6" variant="h6" className={sectionHeading}>
            What is this?
          </Text>
          <Text
            variant="body2"
            color="secondary"
            className="mb-3 whitespace-pre-line leading-relaxed"
          >
            {helpContent.sections.whatIsIt}
          </Text>
        </>
      )}

      {/* Additional Info Sections */}
      {helpContent.sections.additionalInfo &&
        Object.entries(helpContent.sections.additionalInfo).map(
          ([title, content]) => (
            <div key={title} className="mb-3">
              <Text as="h6" variant="h6" className={sectionHeading}>
                {title}
              </Text>
              <Text
                variant="body2"
                color="secondary"
                className="whitespace-pre-line leading-relaxed"
              >
                {content}
              </Text>
            </div>
          )
        )}

      {/* Common Tasks Section */}
      {helpContent.sections.commonTasks &&
        helpContent.sections.commonTasks.length > 0 && (
          <>
            <Divider className="mb-3" />
            <Text as="h6" variant="h6" className={sectionHeading}>
              Common Tasks
            </Text>
            <ul className="mb-2 space-y-1">
              {helpContent.sections.commonTasks.map((task, index) => (
                <li
                  key={index}
                  className="type-body-md text-content-secondary before:mr-2 before:font-bold before:content-['•']"
                >
                  {task}
                </li>
              ))}
            </ul>
          </>
        )}

      {/* Example Scenarios Section */}
      {helpContent.sections.scenarios &&
        helpContent.sections.scenarios.length > 0 && (
          <>
            <Divider className="mb-3" />
            <Text as="h6" variant="h6" className={sectionHeading}>
              Example Scenarios
            </Text>
            <div className="mb-2">
              {helpContent.sections.scenarios.map((scenario, index) => (
                <Accordion key={index} className="mb-2">
                  <AccordionItem
                    value={`scenario-${index}`}
                    title={
                      <span className="flex items-center gap-2">
                        <Chip
                          label={`${index + 1}`}
                          size="sm"
                          className="min-w-7 font-semibold"
                        />
                        <span className="type-h6 font-semibold">
                          {scenario.title}
                        </span>
                      </span>
                    }
                  >
                    <Text variant="body2" className="mb-2 leading-relaxed">
                      {scenario.description}
                    </Text>

                    {scenario.steps && scenario.steps.length > 0 && (
                      <>
                        <Text variant="caption" className="mb-1 font-semibold">
                          Steps:
                        </Text>
                        <ol className="mb-2 list-inside list-decimal space-y-1">
                          {scenario.steps.map((step, stepIndex) => (
                            <li key={stepIndex}>{step}</li>
                          ))}
                        </ol>
                      </>
                    )}

                    {scenario.result && (
                      <>
                        <Text
                          variant="caption"
                          className="mb-0.5 font-semibold"
                        >
                          Result:
                        </Text>
                        <Text variant="body2" className="leading-relaxed">
                          {scenario.result}
                        </Text>
                      </>
                    )}
                  </AccordionItem>
                </Accordion>
              ))}
            </div>
          </>
        )}

      {/* Related Pages Section */}
      {helpContent.sections.relatedPages &&
        helpContent.sections.relatedPages.length > 0 && (
          <>
            <Divider className="mb-3" />
            <Text as="h6" variant="h6" className={sectionHeading}>
              Related Pages
            </Text>
            <ul className="mb-2 space-y-1">
              {helpContent.sections.relatedPages.map((page, index) => {
                const route = getRouteForPage(page.title);
                return (
                  <li key={index} className="py-0.5">
                    <div
                      className={
                        route
                          ? 'group/page flex cursor-pointer items-start'
                          : 'flex items-start'
                      }
                      onClick={() => handlePageClick(page.title)}
                    >
                      <ArrowForwardIcon
                        aria-hidden="true"
                        className="mr-2 mt-1 size-4 text-content-accent"
                      />
                      <div>
                        <Text
                          variant="body2"
                          className="font-semibold transition-colors duration-200 group-hover/page:underline"
                        >
                          {page.title}
                        </Text>
                        <Text
                          variant="body2"
                          color="secondary"
                          className="leading-normal"
                        >
                          {page.description}
                        </Text>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}

      {/* Documentation Link */}
      {helpContent.documentationUrl && (
        <>
          <Divider className="mb-3" />
          <Link
            href={helpContent.documentationUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            className="inline-flex items-center gap-2"
          >
            <ArticleIcon aria-hidden="true" className="size-5" />
            <Text variant="body2" className="font-semibold">
              Read Full Documentation
            </Text>
          </Link>
        </>
      )}
    </div>
  );
}
